import React, { useState } from 'react';

import cn from 'classnames';
import { func, number, shape, string } from 'prop-types';

import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';

import Radio from 'components/Common/Radio';
import Text from 'components/Common/Text';

import { debounceIntervalShort } from 'constants/debounceInterval';
import { ensureArray } from 'utils/arrays';
import sleep from 'utils/sleep';

import s from './ScaleInput.css';

const ScaleInput = ({
  contextId,
  handleDeleteResponse,
  handleSaveResponse,
  initialValue,
  prompt,
  responseId,
  setManualClose,
}) => {
  const [scaleRating, setScaleRating] = useState(initialValue);

  const handleSelectItem = async response => {
    setScaleRating(response);

    // Allows user to see scale clear before closing
    await sleep(debounceIntervalShort);
    handleSaveResponse({ value: response, prompt });
    setManualClose(true);
  };

  const handleDeleteItem = async () => {
    setScaleRating('');

    // Allows user to see scale clear before closing
    await sleep(debounceIntervalShort);
    handleDeleteResponse({ promptId: prompt.id, contextId, responseId });
    setManualClose(true);
  };

  const sortedOptions = ensureArray(prompt.options).sort(
    (a, b) => b.order - a.order,
  );

  const isNotApplicable =
    sortedOptions
      .find(opt => opt.id === scaleRating?.[0])
      ?.value?.toLowerCase()
      .trim() === 'n/a';

  const getSelectedStyling = order => {
    const selectedOption = sortedOptions.find(
      opt => opt.id === scaleRating?.[0],
    );
    return order <= selectedOption?.order;
  };

  return (
    <>
      <Text variant="titleMdBold" mb={1.5}>
        {prompt.text}
      </Text>
      <List className={s.list}>
        {sortedOptions.map((opt, idx) => (
          <ListItemButton
            key={opt.id}
            className={s.listItem}
            onClick={() => handleSelectItem([opt.id])}
          >
            <div
              className={cn(s.scale, {
                [s.selected]: getSelectedStyling(opt.order),
                [s.notApplicable]: isNotApplicable,
                [s.top]: idx === 0,
              })}
            />
            <Radio
              removeOffset
              value={opt.id}
              checked={scaleRating?.[0] === opt.id}
              size="small"
            />
            <Text>{opt.value}</Text>
          </ListItemButton>
        ))}
        <ListItemButton onClick={handleDeleteItem}>Clear</ListItemButton>
      </List>
    </>
  );
};

ScaleInput.propTypes = {
  contextId: number,
  handleDeleteResponse: func.isRequired,
  handleSaveResponse: func.isRequired,
  initialValue: string,
  prompt: shape({}).isRequired,
  responseId: number,
  setManualClose: func.isRequired,
};

ScaleInput.defaultProps = {
  contextId: undefined,
  initialValue: '',
  responseId: undefined,
};

export default ScaleInput;
